import { Select } from "antd";
import { Formik, Form, Field } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ButtonLoader from "@/Components/ButtonLoader";
import FieldErrorMessage from "@/Components/common/FieldErrorMessage";
import { RequiredSymbol } from "@/Components/common/RequiredSymbol";
import { SelectDownIcon } from "@/Components/Icons/SelectDownIcon";
import { SelectRemoveIcon } from "@/Components/Icons/SelectRemoveIcon";
import {
  resetDateAndTime,
  updateActiveStep,
  updateActiveTemplate,
  updateJobData,
  updateSkillsList,
  updateStepsValidated,
  deSelectShortListAllCandidate,
} from "@/reducers/clientcreatejob";
import {
  _fetchRate,
  _fetchSkills,
  _fetchClientMultipleRates,
} from "@/services/createjob";
import { createJobValidations } from "@/utilities/CommonText";
import { showErrorToast, showWarningToast } from "@/utilities/utils";

import ResetButton from "../buttons/ResetButton";
import { SelectTemplate } from "../jobDetails/SelectTemplate";
import { JobShiftLists } from "../JobShiftLists";
import { JobSteps } from "../JobSteps";
/**
 * The JobDetails function is responsible for fetching job details, handling role changes, fetching job rates and skills, and rendering the form for crafting job title and summary.
 *
 * @return {void} This function does not return any value
 */
export const JobDetails = () => {
  const dispatch = useDispatch();
  const {
    jobData,
    candidateRoleList,
    skillsList,
    activeTemplate,
    jobShiftObj,
  } = useSelector((state) => state.clientCreateJob);
  const [rolesFetching, setRolesFetching] = useState(false);
  /**
   * Handles the role change by updating job data with the new role information.
   *
   * @param {type} value - The new value for the role
   * @return {type} undefined
   */
  const handleRoleChange = (value) => {
    let activeRole = candidateRoleList.find((item) => item.id === value);
    dispatch(
      updateJobData({
        ...jobData,
        roleId: value,
        roleName: activeRole.name,
        parentID: activeRole.parent_id,
        skills: [],
      }),
    );
  };

  /**
   * Fetches the rate information asynchronously and updates the job data.
   */

  // NOT USED - not fetching this api after introduce multiple rates for client
  const fetchRate = async () => {
    let response = _fetchRate({
      role_id: jobData.roleId,
      setting_id: jobData.parentID,
    });
    response
      .then((res) => {
        const {
          data: { rate_list, charge_percentage_on_pay_rate },
        } = res;
        let skills = jobData.skills;
        dispatch(
          updateJobData({
            rateObj: rate_list,
            vatStructure: rate_list.vat_structure,
            dayRate: rate_list.is_day_rate,
            client_per: parseFloat(charge_percentage_on_pay_rate),
            client_rate_id: rate_list.id,
            skills: skills,
            // rateCardList: rate_list.length > 1 ? [] : rate_List[0].id
          }),
        );
        if (jobShiftObj.length) {
          dispatch(resetDateAndTime());
          showWarningToast(createJobValidations.fillFormAgain);
        }
        fetchJobSkills();
      })
      .catch((err) => {
        // console.log(err);
        const {
          response: {
            data: { message },
          },
        } = err;
        showErrorToast(message);
        dispatch(
          updateActiveTemplate({
            template: null,
          }),
        );
        dispatch(
          updateJobData({
            ...jobData,
            skills: [],
            roleId: null,
            parentID: null,
          }),
        );
        setRolesFetching(false);
        dispatch(updateSkillsList([]));
      });
  };

  // Multiple rates fetch for client
  const fetchClientMultipleRate = async () => {
    try {
      const res = await _fetchClientMultipleRates({
        role_id: jobData?.roleId,
        setting_id: jobData?.parentID,
      });

      const {
        data: { rate_list },
      } = res;

      const chargePercentageOnPayRate =
        rate_list[0]?.charge_percentage_on_pay_rate || "0.00";
      const clientRateId = rate_list[0].data?.id || 0;
      const clientVATStructure =
        rate_list[0].data?.vat_structure || "std_with_vat";
      const clientDayRate = rate_list[0].is_day_rate;
      const rateCardList = rate_list.length > 1 ? [] : rate_list[0]?.data?.id;
      const clientChargeRateType = rate_list[0]?.charge_rate_type;

      // in getDayWiseRates redux store - findour id's and if matched thus update the client_per if multiple rates find,
      dispatch(
        updateJobData({
          rateObj: rate_list,
          vatStructure:
            rate_list.length > 1 ? "std_with_vat" : clientVATStructure,
          dayRate: rate_list.length > 1 ? 0 : clientDayRate,
          client_per: parseFloat(
            rate_list.length > 1 ? "0.00" : chargePercentageOnPayRate,
          ),
          client_rate_id: parseFloat(rate_list.length > 1 ? 0 : clientRateId),
          rateCardList: rateCardList,
          skills: jobData.skills,
          priceOfVisitPayCharges:
            parseFloat(rate_list[0]?.candidate_visit_pay_percentage) || 0,
          chargeRateType:
            rate_list.length > 1 ? "percentage" : clientChargeRateType,
        }),
      );

      if (jobShiftObj.length) {
        dispatch(resetDateAndTime());
        showWarningToast(createJobValidations.fillFormAgain);
      }

      fetchJobSkills();
    } catch (err) {
      console.error(err);
      const {
        response: {
          data: { message },
        },
      } = err;

      showErrorToast(message, {
        id: "fetchClientMultipleRate",
      });

      dispatch(updateActiveTemplate({ template: null }));
      dispatch(
        updateJobData({
          ...jobData,
          skills: [],
          roleId: null,
          parentID: null,
        }),
      );
      setRolesFetching(false);
      dispatch(updateSkillsList([]));
    }
  };
  /**
   * Fetches job skills and updates the skills list.
   *
   * @param {none} - No parameters
   * @return {Promise} - A Promise that resolves when the skills list is updated
   */
  const fetchJobSkills = async () => {
    let response = _fetchSkills({
      field_type: "filtered_speciality",
      grade_id: jobData.roleId,
      id: jobData.parentID,
    });
    response
      .then((res) => {
        const {
          data: { field_data },
        } = res;
        dispatch(updateSkillsList(field_data));
      })
      .catch((error) => {})
      .finally(() => {
        setRolesFetching(false);
      });
  };

  const initialUpdateRoleChange = useRef(true);
  useEffect(() => {
    if (initialUpdateRoleChange.current) {
      initialUpdateRoleChange.current = false;
      return;
    }
    // fetching rate and skills when roleId and parentID are available or
    if (jobData.roleId && jobData.parentID) {
      setRolesFetching(true);
      // fetchRate();
      setTimeout(() => {
        fetchClientMultipleRate();
        // setRolesFetching(false);
      }, 500);
    }
  }, [jobData.roleId, jobData.parentID]);

  return (
    <div className="JobDetails_Wrapper mb-12 grid gap-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-[1fr,280px] xl:grid-cols-[1fr,330px]">
        <div className="grid h-fit gap-4">
          <JobSteps />
          <div className="rounded-lg bg-white p-6">
            <div className="mb-2 flex justify-between">
              <h4 className="text-[18px] font-medium leading-[normal] text-black">
                Craft title and summary of your job opportunity
              </h4>
              <div className="flex gap-4">
                <ResetButton />
              </div>
            </div>
            <Formik
              initialValues={{
                jobTitle: jobData.jobTitle,
                jobDescription: jobData.jobDescription,
                roleId: jobData.roleId,
                parentID: jobData.parentID,
                skills: jobData.skills,
              }}
              validateOnMount={false}
              validateOnChange={false}
              validateOnBlur={false}
              validate={(values) => {
                let errors = {};
                if (!values.roleId || values.roleId == null) {
                  errors.roleId = createJobValidations.roleId;
                }
                if (!values.jobTitle || values.jobTitle.trim() === "") {
                  errors.jobTitle = createJobValidations.jobTitle;
                }
                if (
                  !values.jobDescription ||
                  values.jobDescription.trim() === ""
                ) {
                  errors.jobDescription = createJobValidations.jobDesc;
                }
                // if (skillsList.length > 0) {
                //   if (values.skills.length === 0) {
                //     errors.skills = createJobValidations.skills;
                //   }
                // }
                return errors;
              }}
              enableReinitialize={true}
              onSubmit={(values, { setSubmitting }) => {
                initialUpdateRoleChange.current = true;
                dispatch(updateActiveStep(2));

                dispatch(
                  updateStepsValidated({
                    selectDates: true,
                  }),
                );
              }}
            >
              {({
                submitForm,
                isSubmitting,
                errors,
                touched,
                isValid,
                values,
                setFieldValue,
                validateForm,
                submitCount,
              }) => (
                <Form>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="flex flex-col gap-3">
                          <label className="label" htmlFor="jobTitle">
                            Job Title
                            <RequiredSymbol />
                          </label>
                          <Field
                            id="jobTitle"
                            name="jobTitle"
                            type="text"
                            placeholder="Enter Job Title"
                            className="ease h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black transition-all duration-200 placeholder:font-medium placeholder:text-txtColor focus:border-primary"
                            onChange={(e) => {
                              setFieldValue("jobTitle", e.target.value);
                              dispatch(
                                updateJobData({
                                  ...jobData,
                                  jobTitle: e.target.value,
                                }),
                              );
                            }}
                            onKeyUp={(e) => {
                              if (touched.jobTitle && errors.jobTitle) {
                                validateForm();
                              }
                            }}
                          />
                        </div>
                        <FieldErrorMessage
                          name="jobTitle"
                          className={"text-red"}
                        />
                      </div>
                      <div>
                        <div className="flex flex-col gap-3">
                          <label className="label" htmlFor="jobTitle">
                            Role
                            <RequiredSymbol />
                          </label>
                          <Select
                            showSearch
                            style={{ width: "100%" }}
                            placeholder="Select Role"
                            optionFilterProp="children"
                            className="ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary"
                            suffixIcon={
                              <SelectDownIcon cls={"text-txtColor"} />
                            }
                            onChange={(value) => {
                              setFieldValue("roleId", value);
                              if (touched.roleId && errors.roleId) {
                                validateForm();
                              }
                              handleRoleChange(value);
                              dispatch(deSelectShortListAllCandidate());
                            }}
                            value={values.roleId}
                          >
                            {candidateRoleList.map((item, index) => {
                              const { id, name } = item;
                              return (
                                <Select.Option value={id} key={index}>
                                  {name}
                                </Select.Option>
                              );
                            })}
                          </Select>
                        </div>
                        <FieldErrorMessage
                          name="roleId"
                          className={"text-red"}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex flex-col gap-3">
                        <label className="label" htmlFor="jobDescription">
                          Job Description
                          <RequiredSymbol />
                        </label>
                        <Field
                          as="textarea"
                          id="jobDescription"
                          name="jobDescription"
                          type="text"
                          placeholder="Enter Job Description"
                          className="ease max-h-[400px] min-h-[64px] w-full rounded-lg border border-lightGrey px-4 py-3 text-[14px] font-medium leading-[normal] text-black transition-all duration-200 placeholder:font-medium placeholder:text-txtColor focus:border-primary"
                          onChange={(e) => {
                            setFieldValue("jobDescription", e.target.value);
                            dispatch(
                              updateJobData({
                                ...jobData,
                                jobDescription: e.target.value,
                              }),
                            );
                          }}
                          onKeyUp={(e) => {
                            if (
                              touched.jobDescription &&
                              errors.jobDescription
                            ) {
                              validateForm();
                            }
                          }}
                          value={values.jobDescription}
                        />
                      </div>
                      <FieldErrorMessage
                        name="jobDescription"
                        className={"text-red"}
                      />
                    </div>
                    {skillsList.length > 0 && (
                      <div>
                        <div className="flex flex-col gap-3">
                          <label className="label" htmlFor="primarySpeciality">
                            Skills
                          </label>
                          <Select
                            showSearch
                            style={{ width: "100%" }}
                            placeholder="Select Skills"
                            optionFilterProp="children"
                            className="ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey py-2 text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary"
                            suffixIcon={
                              <SelectDownIcon cls={"text-txtColor"} />
                            }
                            removeIcon={<SelectRemoveIcon />}
                            mode="multiple"
                            onChange={(value) => {
                              setFieldValue("skills", value);
                              dispatch(
                                updateJobData({
                                  ...jobData,
                                  skills: value,
                                }),
                              );
                              if (submitCount) {
                                validateField("skills");
                              }
                              if (touched.skills && errors.skills) {
                                validateForm();
                              }
                            }}
                            value={values.skills}
                          >
                            {skillsList.map((item, index) => {
                              const { id, name } = item;
                              return (
                                <Select.Option value={id} key={index}>
                                  {name}
                                </Select.Option>
                              );
                            })}
                          </Select>
                        </div>
                        <FieldErrorMessage
                          name="skills"
                          className={"text-red"}
                        />
                      </div>
                    )}
                    <div className="flex justify-end">
                      <button
                        className={`ease relative h-[40px] w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium leading-[38px] text-white transition-all duration-200 hover:bg-none hover:text-primary ${rolesFetching ? "pointer-events-none" : ""} `}
                        onClick={() => {
                          if (!rolesFetching) {
                            submitForm();
                          }
                        }}
                        type="button"
                        disabled={isSubmitting || !isValid}
                      >
                        {isSubmitting ? <ButtonLoader /> : <span>Next</span>}
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
        <SelectTemplate />
      </div>
      {jobData.totalDays > 0 && jobShiftObj.length ? <JobShiftLists /> : null}
    </div>
  );
};
